import {Component, Input, TemplateRef, ViewChild} from '@angular/core';
import {SubscribedComponent} from "../../../subscribed.component";
import {DegreeType, Experience, ExperienceType} from "../experience";
import {AbstractControl, FormBuilder, FormGroup} from "@angular/forms";
import {BSValidators} from "../../../shared/bsvalidators";
import {ColDef, ColType} from "../../../interfaces/colDef";
import {Globals} from "../../../shared/Globals";

@Component({
    selector: 'bst-experience-editor',
    templateUrl: './experience-editor.component.html',
    styleUrls: ['../experience-assigner.component.scss'],
    standalone: false
})
export class ExperienceEditorComponent extends SubscribedComponent{

  @Input() experience: Experience;
  @Input() opento: Experience = null;
  @Input() type: ExperienceType = ExperienceType.CustomClient;
  @ViewChild('enddate', {static: true}) enddateTemplate: TemplateRef<any>;
  @ViewChild('responsibilities', {static: true}) responsibilitiesTemplate: TemplateRef<any>;

  ExperienceType = ExperienceType;
  DegreeType = DegreeType;

  public form: FormGroup;
  public projectdefs: ColDef[] = [];
  public roledefs: ColDef[] = [];
  public couldEdit = (col: ColDef, values: Experience|null, value: any) => {
    return (col && (col.name === 'items' || col.name === 'url' || col.name === 'description'))
      || !values || values.type === ExperienceType.CustomPosition
      || values.type === ExperienceType.CustomProject
      || values.type === ExperienceType.CustomClient
      || values.type === ExperienceType.CustomResponsibility;
  };
  public couldDelete = (values: Experience) => {
    return this.couldEdit(null, values, null);
  }

  constructor(private fb: FormBuilder) {
    super();
  }

  private makeExperience(): Experience{
    return {
      id: 0,
      name: "",
      description: "",
      start: null,
      end: null,
      location: null,
      position: null,
      type: this.type,
      degree: this.type === ExperienceType.School ? DegreeType.Associate : null,
      degree_finished: this.type === ExperienceType.School ? true : null,
      degree_major: null,
      is_tenant_root: false,
      logo: null,
      items: []
    };
  }

  get iscustom(){
    return this.experience?.type === ExperienceType.CustomClient || this.experience?.type === ExperienceType.School;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.roledefs = [
      {
        name: 'name',
        type: ColType.STRING,
        required: true,
        title: 'Position title',
        length: 100,
        filter: false,
        sort: false
      },
      {
        name: 'location',
        type: ColType.LOCATION,
        title: 'Position location',
        required: false,
        length: 200,
        filter: false,
        sort: false
      },
      {
        name: 'start',
        title: 'Start date',
        type: ColType.DATE,
        required: true,
        filter: false,
        sort: false
      },
      {
        name: 'end',
        title: 'End date',
        type: ColType.CUSTOM,
        required: false,
        filter: false,
        sort: false,
        display: this.enddateTemplate
      },
      {
        name: 'description',
        type: ColType.AREA,
        required: false,
        title: 'Description',
        length: 2000,
        filter: false,
        sort: false
      },
      {
        name: 'items',
        type: ColType.CUSTOM,
        required: false,
        title: 'Achievements / responsibilities',
        filter: false,
        sort: false,
        display: this.responsibilitiesTemplate
      }
    ];

    this.projectdefs = [
      {
        name: 'name',
        type: ColType.STRING,
        required: true,
        title: 'Project name',
        length: 100,
        filter: false,
        sort: false
      },
      {
        name: 'position',
        type: ColType.STRING,
        length: 100,
        required: true,
        title: 'Project role',
        subtitle: 'Enter role',
        filter: false,
        sort: false
      },
      {
        name: 'location',
        title: 'Project location',
        type: ColType.LOCATION,
        length: 255,
        required: false,
        filter: false,
        sort: false
      },
      {
        name: 'start',
        title: 'Start date',
        type: ColType.DATE,
        required: true,
        filter: false,
        sort: false
      },
      {
        name: 'end',
        title: 'End date',
        type: ColType.CUSTOM,
        required: false,
        filter: false,
        sort: false,
        display: this.enddateTemplate
      },
      {
        name: 'company',
        type: ColType.STRING,
        required: false,
        title: 'Project client',
        length: 100,
        filter: false,
        sort: false
      },
      {
        name: 'description',
        type: ColType.AREA,
        required: false,
        title: 'Description',
        length: 2000,
        filter: false,
        sort: false
      },
      {
        name: 'items',
        type: ColType.CUSTOM,
        required: false,
        title: 'Achievements / responsibilities',
        filter: false,
        sort: false,
        display: this.responsibilitiesTemplate
      }
    ];

    if(!this.experience){
      this.experience = this.makeExperience();
    }

    const name = this.experience.name && this.isSchool ? {id:-1, name: this.experience.name} : this.experience.name;
    this.form = this.fb.group({
      name: [{value: name, disabled: !this.iscustom}, [BSValidators.required]],
      position: [{value: this.experience.position, disabled: !this.iscustom}, !this.isSchool ? [BSValidators.required] : []],
      location: [{value: this.experience.location, disabled: !this.iscustom}, []],
      start: [{value: Globals.fromDateString(this.experience.start), disabled: !this.iscustom}, [BSValidators.required]],
      end: [{value: Globals.fromDateString(this.experience.end), disabled: !this.iscustom}, []],
      degree: [this.experience.degree, this.isSchool ? [BSValidators.required] : []],
      degree_major: [this.experience.degree_major, this.isSchool ? [BSValidators.required] : []],
      degree_finished: [this.experience.degree_finished ? 1 : 0, this.isSchool ? [BSValidators.required] : []],
      description: [this.experience.description, []],
      type: this.experience.type,
      is_tenant_root: this.experience.is_tenant_root,
      id: this.experience.id,
      logo: this.experience.logo,
      ongoing: [{value: this.experience.end == null, disabled: !this.iscustom}, []],
      roles: [this.experience.items.filter(o => this.isRole(o)), []],
      projects: [this.experience.items.filter(o => this.isProject(o)), []],
      items: [this.experience.items.filter(o => this.isResponsiblity(o)), []]
    });
  }

  public get isSchool(){
    return this.experience?.type === ExperienceType.School;
  }

  public isResponsiblity(item: Experience){
    return item.type === ExperienceType.CustomResponsibility;
  }

  public isRole(item: Experience): boolean{
    return item.type === ExperienceType.CustomPosition;
  }

  public get isNew(){
    return this.experience && this.experience.id === 0;
  }

  public get isMyCompany(){
    return this.experience && (this.experience.type !== ExperienceType.CustomClient && this.experience.type !== ExperienceType.School);
  }

  public isProject(item: Experience){
    return item.type === ExperienceType.Project || item.type === ExperienceType.CustomProject;
  }

  public isCustom(item: FormGroup): boolean{
    const type = item.get('type').value;
    return type === ExperienceType.CustomPosition
      || type === ExperienceType.CustomProject
      || type === ExperienceType.CustomClient
      || type === ExperienceType.CustomResponsibility
      || type === ExperienceType.School;
  }

  get value(): Experience{
    const master = this.form.getRawValue();
    if(typeof master.name === 'object'){
      master.name = master.name.name ?? null;
    }
    if(master.items){
      master.items.map(o => o.type = ExperienceType.CustomResponsibility);
    }
    master.items = master.items.concat(master.projects).concat(master.roles).filter(o => o);
    if(!master.id){
      master.id = -1;
    }
    delete master.projects;
    delete master.roles;
    delete master.ongoing;
    return master;
  }

  ongoing(checked: boolean, target: AbstractControl) {
    if(checked){
      target.setValue(null);
      target.disable();
    }
    else{
      target.enable();
    }
  }

  markRes($event: Experience | (Experience & null)) {
    if($event){
      if($event.items) {
        $event.items.map(o => {
          o.type = ExperienceType.CustomResponsibility;
        });
      }
      else{
        $event.items = [];
      }
    }
  }
}
